import React, { useEffect } from "react";

import isEqual from "lodash/isEqual";
import { useForm } from "react-final-form";
import MultiSelectField2 from "src/components/Form/MultiSelectField/MultiSelectField2";
import SimpleDatePickerField from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import SingleSelectField from "src/components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import { formatCADate } from "src/views/CampaignDetail/utils";
import { ReportOption } from "../definitions";
import { useWidgetFetch } from "../hooks/useWidgetFetch";
import { checkQueryMultiParams } from "../utils";
import { ALL_BRANCHES_LIST, MAX_OPTION_VALUES_DISPLAY } from "./constants";

import { IListOption } from "src/store/models/listOption.models";
import * as S from "../../../constants/StringConstants";

const SELECT_ALL_VALUE = "select_all";

const checkDefaultOrPresetValues = (option, data, updater: (name: string, value: any) => void) => {
  const { name, defaultValue, defaultValueOther } = option;
  if (defaultValue) {
    console.log("static preselect not yet implemented", defaultValue);
  }
  if (!data || data.length === 0) {
    return;
  }

  const fromParams = checkQueryMultiParams(name);
  if (fromParams.length > 0) {
    updater(
      name,
      data.filter(x => fromParams.includes(x.value)),
    );
    return;
  }

  if (defaultValueOther) {
    switch (defaultValueOther) {
      case "first":
        updater(name, data.slice(0, 1));
        break;
      case "all":
        updater(name, data);
        break;
      default:
        if (typeof defaultValueOther === "object") {
          updater(
            name,
            data.filter(x => defaultValueOther.includes(x.value)),
          );
        }
        break;
    }
  }
};

const MultiSelectDynamicOptionRenderer = ({ input, option }: { input: any; option: ReportOption }) => {
  const { name, label, optionsDataSource, defaultValue, defaultValueOther } = option;
  const { data } = useWidgetFetch(option.name, optionsDataSource);

  const form = useForm();

  useEffect(() => {
    checkDefaultOrPresetValues(option, data, form.change);
  }, [data]);

  const showSelectAll = option.config?.showSelectAll ?? false;
  const onChange = (event: any, value: IListOption[]) => {
    const isAddAll = value.find(v => v.value === SELECT_ALL_VALUE);
    if (isAddAll) {
      if (name === "branch") {
        return ALL_BRANCHES_LIST.map(br => data.find(v => v.value === br)).filter(v => v);
      } else {
        return data;
      }
    }
    return value;
  };
  return (
    <MultiSelectField2
      name={name}
      label={label}
      onChange={onChange}
      options={[...(showSelectAll ? [{ key: S.SELECT_ALL, value: SELECT_ALL_VALUE }] : []), ...data]}
    />
  );
};

export const SingleSelectOptionRenderer = ({ option }: { option: ReportOption }) => {
  const { name, label, options } = option;
  return (
    <SingleSelectField
      name={name}
      label={label}
      options={options}
      parse={undefined}
      inputProps={{ style: { minWidth: "200px" } }}
    />
  );
};

export const SingleSelectDynamicOptionRenderer = ({ option }: { option: ReportOption }) => {
  const { name, label, optionsDataSource } = option;
  const { data } = useWidgetFetch(name, optionsDataSource);
  return (
    <SingleSelectField
      name={name}
      label={label}
      options={data}
      inputProps={{
        style: { minWidth: "200px", marginBottom: "-8px", borderRadius: "12px" },
      }}
    />
  );
};

export const DatePickerOptionRenderer = ({ option }: { option: ReportOption }) => {
  const { name, label } = option;
  return <SimpleDatePickerField name={name} label={label} className="date-picker" />;
};

export const TextFieldOptionRenderer = ({ option }: { option: ReportOption }) => {
  const { name, label } = option;
  return (
    <TextField name={name} label={label} inputProps={{ style: { marginBottom: "-12px", borderRadius: "12px" } }} />
  );
};

export const renderOption = (option: ReportOption, index: number) => {
  const allOptionRenderers: Record<string, React.FC<{ option: ReportOption }>> = {
    date: DatePickerOptionRenderer,
    text: TextFieldOptionRenderer,
    select: SingleSelectOptionRenderer,
    "select-dynamic": SingleSelectDynamicOptionRenderer,
    "multi-select-dynamic": MultiSelectDynamicOptionRenderer,
  };
  const OptionRenderer = allOptionRenderers[option.dataType];
  return (
    <div className="report__option" key={index}>
      <OptionRenderer option={option} />
    </div>
  );
};

export const RenderOptionValue: React.FC<{ option: ReportOption; index: number; values: any }> = ({
  option,
  index,
  values,
}) => {
  const { name, optionsDataSource } = option;
  const { data } = useWidgetFetch(name, optionsDataSource);
  const [newValues, setNewValues] = React.useState<any>(values);

  useEffect(() => {
    checkDefaultOrPresetValues(option, data, (name, value) => {
      setNewValues({ ...newValues, [name]: value });
    });
  }, [data]);

  useEffect(() => {
    setNewValues({ ...newValues, [name]: values[name] });
  }, [values[name]]);

  let value = "";
  switch (option.dataType) {
    case "date":
      if (newValues[option.name]) {
        value = formatCADate(newValues[option.name]);
      }
      break;
    case "multi-select-dynamic":
      if (Array.isArray(newValues[option.name])) {
        if (
          option.name === "branch" &&
          isEqual(newValues[option.name].map(({ value }) => value).sort(), ALL_BRANCHES_LIST)
        ) {
          value = "All";
        } else {
          const num = newValues[option.name].length;
          if (num > MAX_OPTION_VALUES_DISPLAY) {
            value =
              newValues[option.name]
                .slice(0, MAX_OPTION_VALUES_DISPLAY)
                .map((v: any) => v.key)
                .join(", ") + ` ... (+${num - MAX_OPTION_VALUES_DISPLAY} more)`;
          } else {
            value = newValues[option.name]
              .map((v: any) => v.key)
              .filter(x => !!x)
              .join(", ");
          }
        }
      }
      break;
    case "select":
      value = option.options?.find(o => o.value === newValues[option.name])?.key || "";
      break;
    case "select-dynamic":
      value = data?.find(o => o.value === parseInt(newValues[option.name]))?.key || "";
      break;
    default:
      value = newValues[option.name];
      break;
  }
  return (
    <div className="report__option">
      <div>
        <strong>{option.label}</strong>: {value}
      </div>
    </div>
  );
};

export const RenderOptionValues = ({ sourceOptions, values }: { sourceOptions: ReportOption[]; values: any }) => {
  let options = [...sourceOptions.filter(opt => opt.config?.hideInPrint !== true)];

  const startDate = options.find(option => option.name === "startDate");
  const endDate = options.find(option => option.name === "endDate");

  if (startDate && endDate) {
    const rangeOption: ReportOption = {
      name: "dateRange",
      label: "Range",
      dataType: "text",
    };
    values[rangeOption.name] = [formatCADate(values[startDate.name]), formatCADate(values[endDate.name])].join(" - ");
    options.splice(options.indexOf(startDate), 1, rangeOption);
    options.splice(options.indexOf(endDate), 1);
  }

  return (
    <>
      {options.map((option, index) => {
        return <RenderOptionValue key={option.name} option={option} index={index} values={values} />;
      })}
    </>
  );
};
